import { useQuery } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  BulkInviteResults,
  CreateTenantUserInviteMutation,
  CreateTenantUserInviteMutationVariables,
  CreateTenantUserInvitesMutation,
  CreateTenantUserInvitesMutationVariables,
  DeactivateUserMutation,
  DeactivateUserMutationVariables,
  InvitableUserTenantRole,
  ListTenantUserInvitesQuery,
  ListTenantUserInvitesQueryVariables,
  ListTenantUsersQuery,
  ListTenantUsersQueryVariables,
  RevokeTenantUserInviteMutation,
  RevokeTenantUserInviteMutationVariables,
  UserSortBy,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ErrorCard from "~frontend/components/ErrorCard";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox } from "~frontend/components/Table";
import InviteDialog from "~frontend/components/users/InviteDialog";
import UserInviteTable from "~frontend/components/users/UserInviteTable";
import UserListControls from "~frontend/components/users/UserListControls";
import UserTable from "~frontend/components/users/UserTable";
import {
  SUBACCOUNT_ROLE_DISPLAY_NAMES,
  TENANT_ROLE_DISPLAY_NAMES,
  USER_INVITE_SORTS,
  USER_SORTS,
} from "~frontend/components/users/constants";
import {
  TenantUser,
  TenantUserInvite,
  UserStatus,
} from "~frontend/components/users/types";
import {
  CREATE_TENANT_USER_INVITE,
  CREATE_TENANT_USER_INVITES,
  DEACTIVATE_USER,
  LIST_TENANT_USERS,
  LIST_TENANT_USER_INVITES,
  REVOKE_TENANT_USER_INVITE,
} from "~frontend/graphql/users";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUser } from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

function UserOrInviteRole({
  userOrInvite,
}: {
  userOrInvite: TenantUser | TenantUserInvite;
}): ReactElement {
  if (
    userOrInvite.tenantRole === UserTenantRole.None &&
    userOrInvite.subaccountMemberships.length > 0
  ) {
    return (
      <ul style={{ listStyle: "none", paddingLeft: 0 }}>
        {userOrInvite.subaccountMemberships.map(subaccountMembership => (
          <li key={subaccountMembership.subaccount.id}>
            {SUBACCOUNT_ROLE_DISPLAY_NAMES[subaccountMembership.role]} (
            <InternalLink
              to={`/subaccounts/${subaccountMembership.subaccount.slug}/users`}
            >
              {subaccountMembership.subaccount.name}
            </InternalLink>
            )
          </li>
        ))}
      </ul>
    );
  } else {
    return <>{TENANT_ROLE_DISPLAY_NAMES[userOrInvite.tenantRole]}</>;
  }
}

export default function OrganizationUsers(): ReactElement {
  useBreadcrumbs([{ label: "People", path: "/organization/users" }]);
  const snackbar = useSnackbar();

  const currentUser = useCurrentUser()!;
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "pageSize",
    withDefault(NumberParam, 10)
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    "status",
    withDefault(StringParam, UserStatus.ACTIVE)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(
      StringParam,
      statusFilter === UserStatus.INVITED
        ? USER_INVITE_SORTS[0].value
        : USER_SORTS[0].value
    )
  );

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [bulkInviteResults, setBulkInviteResults] =
    useState<BulkInviteResults | null>(null);

  const listUserVariables = {
    input: {
      sort: sort as UserSortBy,
      filter: { nameOrEmailContains: search },
      limit: pageSize,
      offset: page * pageSize,
    },
  };

  const listUsersResult = useQuery<
    ListTenantUsersQuery,
    ListTenantUsersQueryVariables
  >(LIST_TENANT_USERS, {
    variables: listUserVariables,
    pollInterval: 60 * 1000,
    fetchPolicy: "network-only",
  });

  const listUserInvitesResult = useQuery<
    ListTenantUserInvitesQuery,
    ListTenantUserInvitesQueryVariables
  >(LIST_TENANT_USER_INVITES, {
    variables: listUserVariables,
    pollInterval: 60 * 1000,
    fetchPolicy: "network-only",
  });

  const [createTenantUserInvite] = useMutation<
    CreateTenantUserInviteMutation,
    CreateTenantUserInviteMutationVariables
  >(CREATE_TENANT_USER_INVITE, {
    refetchQueries: [LIST_TENANT_USERS, LIST_TENANT_USER_INVITES],
    awaitRefetchQueries: true,
  });

  const [createTenantUserInvites] = useMutation<
    CreateTenantUserInvitesMutation,
    CreateTenantUserInvitesMutationVariables
  >(CREATE_TENANT_USER_INVITES, {
    refetchQueries: [LIST_TENANT_USERS, LIST_TENANT_USER_INVITES],
    awaitRefetchQueries: true,
  });

  const [revokeTenantUserInvite] = useMutation<
    RevokeTenantUserInviteMutation,
    RevokeTenantUserInviteMutationVariables
  >(REVOKE_TENANT_USER_INVITE, {
    refetchQueries: [
      { query: LIST_TENANT_USER_INVITES, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const [deactivateUser] = useMutation<
    DeactivateUserMutation,
    DeactivateUserMutationVariables
  >(DEACTIVATE_USER, {
    refetchQueries: [
      { query: LIST_TENANT_USERS, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const handleInviteUser = useCallback(
    async ({
      email,
      fullName,
      role,
      emails,
      multipleInvitesEnabled,
    }: {
      email?: string;
      fullName?: string;
      role: InvitableUserTenantRole;
      emails?: string[];
      multipleInvitesEnabled?: boolean;
    }) => {
      if (multipleInvitesEnabled && emails) {
        const { data, errors } = await createTenantUserInvites({
          variables: {
            input: {
              emails,
              tenantRole: role,
            },
          },
        });

        if (errors) {
          snackbar.enqueueSnackbar(
            `Error inviting users: ${errors[0].message}`,
            { variant: "error" }
          );
          throw errors;
        }
        // switch dialog to "results" panel
        setBulkInviteResults(data!.createTenantUserInvites);
      } else {
        const { data, errors } = await createTenantUserInvite({
          variables: {
            input: {
              email: email as string,
              fullName: fullName as string,
              tenantRole: role,
            },
          },
        });

        if (errors) {
          snackbar.enqueueSnackbar(
            `Error inviting user: ${errors[0].message}`,
            {
              variant: "error",
            }
          );
          throw errors;
        } else if (data?.createTenantUserInvite.user) {
          snackbar.enqueueSnackbar(
            `Role added to user ${data.createTenantUserInvite.user.fullName}`
          );
        } else {
          snackbar.enqueueSnackbar(`User ${fullName} invited`);
        }
      }
    },
    [createTenantUserInvite, createTenantUserInvites, snackbar]
  );

  const handleRevokeInvite = useCallback(
    async (userInvite: TenantUserInvite) => {
      const { errors } = await revokeTenantUserInvite({
        variables: { input: { userInviteId: userInvite.id } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(
          `Error revoking invite: ${errors[0].message}`,
          { variant: "error" }
        );
      } else {
        snackbar.enqueueSnackbar(`Invite revoked for ${userInvite.email}`);
      }
    },
    [revokeTenantUserInvite, snackbar]
  );

  const handleDeactivateUser = useCallback(
    async (user: TenantUser) => {
      const { errors } = await deactivateUser({
        variables: { input: { userId: user.id } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(
          `Error deactivating user: ${errors[0].message}`,
          { variant: "error" }
        );
      } else {
        snackbar.enqueueSnackbar(`User ${user.email} deactivated`);
      }
    },
    [deactivateUser, snackbar]
  );

  const renderTable = () => {
    switch (statusFilter) {
      case UserStatus.INVITED:
        if (listUserInvitesResult.loading) {
          return <Loading />;
        }
        if (listUserInvitesResult.error) {
          return <ErrorCard errors={[listUserInvitesResult.error]} />;
        }
        return (
          <UserInviteTable
            userInvites={
              listUserInvitesResult.data!.listTenantUserInvites.userInvites
            }
            totalCount={
              listUserInvitesResult.data!.listTenantUserInvites.totalCount
            }
            page={page}
            onChangePage={setPage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={newPageSize => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            renderRole={userInvite => (
              <UserOrInviteRole userOrInvite={userInvite} />
            )}
            renderActions={userInvite => [
              {
                id: "revoke",
                icon: <DeleteIcon />,
                tooltipLabel: "Revoke invite",
                onClick: () => handleRevokeInvite(userInvite),
                confirm: {
                  title: "Revoke invite",
                  description: `Revoke invite to ${userInvite.email}?`,
                },
              },
            ]}
          />
        );
      case UserStatus.ACTIVE:
        if (listUsersResult.loading) {
          return <Loading />;
        }
        if (listUsersResult.error) {
          return <ErrorCard errors={[listUsersResult.error]} />;
        }
        return (
          <UserTable
            users={listUsersResult.data!.listTenantUsers.users}
            totalCount={listUsersResult.data!.listTenantUsers.totalCount}
            page={page}
            onChangePage={setPage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={setPageSize}
            renderRole={user => <UserOrInviteRole userOrInvite={user} />}
            renderActions={user => [
              {
                id: "deactivate",
                icon: <DeleteIcon />,
                tooltipLabel: "Deactivate",
                onClick: () => handleDeactivateUser(user),
                confirm: {
                  title: "Deactivate user",
                  description: `Deactivate ${user.email}?`,
                },
                disabled:
                  user.tenantRole === UserTenantRole.Staff ||
                  user.id === currentUser.id,
              },
            ]}
          />
        );
    }
  };

  return (
    <PageContainer>
      <PageHeader>People</PageHeader>
      <UserListControls
        statusFilter={statusFilter as UserStatus}
        setStatusFilter={newStatusFilter => {
          setStatusFilter(newStatusFilter);
          setPage(0);
        }}
        search={search}
        setSearch={newSearch => {
          setSearch(newSearch);
          setPage(0);
        }}
        sort={sort}
        setSort={setSort}
        onClickInvite={() => setInviteDialogOpen(true)}
      />
      <TableBox sx={{ pb: 1 }}>
        <AsyncWrapper
          loading={[listUserInvitesResult.loading, listUsersResult.loading]}
          errors={[listUserInvitesResult.error, listUsersResult.error]}
        >
          {renderTable()}
        </AsyncWrapper>
      </TableBox>
      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false);
          setBulkInviteResults(null);
        }}
        onSubmit={handleInviteUser}
        roleOptions={[
          { value: InvitableUserTenantRole.Owner, label: "Organization Owner" },
        ]}
        defaultRole={InvitableUserTenantRole.Owner}
        enableMultipleInvites={true}
        bulkInviteResults={bulkInviteResults}
      />
    </PageContainer>
  );
}
